/**
 * Notification presentation helpers, shared by the desktop bell and the mobile
 * notifications screen so the two cannot drift apart.
 *
 * Pure functions only: no fetch, no DOM, no React.
 */

/** Relative time. Falls back to the raw string when the date cannot be read. */
export function timeAgo(value, now = Date.now()) {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return String(value);
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.round(days / 7);
  if (weeks < 5) return `${weeks} wk ago`;
  return new Date(then).toLocaleDateString();
}

/**
 * Severity colour for a notification. The backend does not send a severity field
 * on in_app_notifications, so this is derived from `kind`, which it does send —
 * and unknown kinds get the neutral tone rather than being guessed at.
 */
export const SEVERITY = Object.freeze({
  critical: { key: 'critical', className: 'text-danger', dot: 'bg-danger' },
  warning: { key: 'warning', className: 'text-warn', dot: 'bg-warn' },
  info: { key: 'info', className: 'text-ink-muted', dot: 'bg-ink-subtle' },
});

const CRITICAL_KINDS = /(critical|recall|failed|expired|breach)/i;
const WARNING_KINDS = /(assigned|corrective|alert|hold|exception|question|review)/i;

export function severityFor(notification) {
  const kind = String(notification?.kind || '');
  if (CRITICAL_KINDS.test(kind)) return SEVERITY.critical;
  if (WARNING_KINDS.test(kind)) return SEVERITY.warning;
  return SEVERITY.info;
}

/**
 * The link a notification opens, when it has one. Mirrors the mobile screen's
 * routing so both shells land in the same place; returns null when there is none,
 * rather than sending the user somewhere unrelated.
 */
export function linkFor(notification, { mobile = false } = {}) {
  const kind = String(notification?.kind || '');
  if (kind === 'inspection_corrective_action_assigned') {
    return mobile ? '/mobile/safety/inspections' : '/safety';
  }
  if (kind === 'vision_concern_assigned') {
    const base = mobile ? '/mobile/visual-safety' : '/visual-safety';
    return notification.concern_id
      ? `${base}?concern=${encodeURIComponent(notification.concern_id)}`
      : base;
  }
  if (kind === 'invoice_review') {
    if (!mobile) {
      return notification.invoice_id ? `/invoices/${encodeURIComponent(notification.invoice_id)}` : '/invoices';
    }
    return notification.invoice_id
      ? `/mobile/invoices/${encodeURIComponent(notification.invoice_id)}`
      : '/mobile/invoices';
  }
  if (kind === 'operandi_human_context_question') {
    const base = mobile ? '/mobile/process-discovery' : '/process-discovery';
    return notification.run_id ? `${base}?runId=${encodeURIComponent(notification.run_id)}` : base;
  }
  return null;
}

/** Newest first, and never more than `limit` rows for a dropdown. */
export function latest(notifications, limit = 20) {
  return [...(notifications || [])]
    .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
    .slice(0, limit);
}
